import React, { useCallback, useEffect, useMemo } from "react";
import { DndContext, closestCenter } from "@dnd-kit/core";
import {
  SortableContext,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import {
  flexRender,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table";
import Cell from "./Cell";
import SortableHeader from "./SortableHeader";
import useColumnOrder from "./hooks/useColumnOrder";
import useHorizontalDragScroll from "./hooks/useHorizontalDragScroll";
import { Pagination } from "../../../../components/shared/Pagination/Pagination";
import Spinner from "../../../../components/shared/spinner/Spinner";
import { useSwingLibraryStore } from "../../store/swingLibraryStore";
import AvgRows from "./AvgRows/AvgRows";

const isComponent = (component) => Boolean(component);

// interface BaseSwingTableProps {
//   allVideos: VideoT[]
//   columns: ColumnDef<VideoT>[]
//   additionalRows?: AdditionalRow[]
//   containerClassName?: string
//   enableRowSelection?: boolean
//   isLoading?: boolean
//   showPagination?: boolean
//   tableClassName?: string
//   getRowClassName?: (row: Row<VideoT>) => string
//   onRowRender?: (row: Row<VideoT>) => ReactNode
// }

export default function BaseSwingTable({
  additionalRows = [],
  allVideos,
  averages,
  columns,
  containerClassName = "",
  enableRowSelection = true,
  getRowClassName,
  isLoading = false,
  onRowRender,
  showPagination = false,
  tableClassName = "",
}) {
  const {
    pagination,
    rowSelection,
    selectedVideos,
    setPagination,
    setRowSelection,
    setSelectedVideos,
    quickCompareCollection1,
  } = useSwingLibraryStore();

  const disabledIds = useMemo(() => {
    if (!quickCompareCollection1?.videoIds) return new Set();
    return new Set(quickCompareCollection1.videoIds);
  }, [quickCompareCollection1]);

  // Initial column order from column defs
  const initialOrder = useMemo(
    () => columns.map((c, idx) => String(c.id ?? idx)),
    [columns]
  );
  const [columnOrder, handleDragEnd] = useColumnOrder(initialOrder);

  // Horizontal drag-scroll container
  const dragScroll = useHorizontalDragScroll();

  const handleRowSelectionChange = useCallback(
    (value) => {
      if (!enableRowSelection) return;
      const nextValue =
        typeof value === "function" ? value(rowSelection) : value;
      setRowSelection(nextValue);
    },
    [enableRowSelection, rowSelection, setRowSelection]
  );

  const enableRowSelectionConfig = useMemo(() => {
    if (!enableRowSelection) return false;
    if (!disabledIds || disabledIds.size === 0) return true;
    return (row) => !disabledIds.has(row?.original?.id);
  }, [enableRowSelection, disabledIds]);

  // TanStack table
  const table = useReactTable({
    columnResizeDirection: "ltr",
    columnResizeMode: "onChange",
    columns,
    data: allVideos || [],
    enableRowSelection: enableRowSelectionConfig,
    getCoreRowModel: getCoreRowModel(),
    onRowSelectionChange: enableRowSelection
      ? handleRowSelectionChange
      : undefined,
    state: {
      columnOrder,
      ...(enableRowSelection ? { rowSelection } : {}),
    },
    getRowId: (row) => String(row?.id),
  });

  // Compute selectedTableData internally when row selection is enabled
  const selectedTableData = useMemo(() => {
    if (!enableRowSelection) return [];
    return Object.values(selectedVideos || {});
  }, [enableRowSelection, selectedVideos]);

  useEffect(() => {
    if (!enableRowSelection) return;

    const selectionKeys = rowSelection ? Object.keys(rowSelection) : [];

    if (selectionKeys.length === 0) {
      if (selectedVideos && Object.keys(selectedVideos).length > 0) {
        setSelectedVideos({});
      }
      return;
    }

    if (!Array.isArray(allVideos) || allVideos.length === 0) {
      return;
    }

    const nextSelected = {};
    let hasChanges = false;

    allVideos.forEach((video) => {
      const videoId = video?.id;
      if (!videoId) {
        return;
      }

      const isSelected = Boolean(rowSelection?.[videoId]);

      if (isSelected) {
        nextSelected[videoId] = video;
        if (selectedVideos?.[videoId] !== video) {
          hasChanges = true;
        }
      } else if (selectedVideos?.[videoId]) {
        hasChanges = true;
      }
    });

    const lengthsDiffer =
      Object.keys(nextSelected).length !==
      Object.keys(selectedVideos || {}).length;

    if (hasChanges || lengthsDiffer) {
      setSelectedVideos(nextSelected);
    }
  }, [
    allVideos,
    rowSelection,
    enableRowSelection,
    selectedVideos,
    setSelectedVideos,
  ]);

  // Default row renderer
  const defaultRowRender = (row) => {
    const cells = row.getVisibleCells();
    const tagsIdx = cells.findIndex((c) => c.column.id === "tags");
    const remainingCols = Math.max(0, cells?.length - (tagsIdx + 1));

    const isNotAnalyzed =
      Object.keys(row.original?.analysis ?? {}).length === 0;

    // Build className: base classes + optional additional classes
    const baseClassName = isNotAnalyzed ? "bg-gray50" : "";
    const additionalClassName = getRowClassName ? getRowClassName(row) : "";
    const rowClassName = [baseClassName, additionalClassName]
      .filter(Boolean)
      .join(" ");

    return (
      <tr className={rowClassName} key={row.id} style={{ height: 75 }}>
        {isNotAnalyzed ? (
          <>
            {/* cells up to and including 'tags' */}
            {cells.slice(0, tagsIdx + 1).map((cell) => (
              <Cell cell={cell} key={cell.id} />
            ))}

            {/* banner right after 'tags' */}
            {remainingCols > 0 && (
              <td
                className="px-2"
                colSpan={remainingCols}
                key={`${row.id}-not-analyzed`}
              >
                <div className="bg-warning100 flex w-full min-w-0 items-center justify-center rounded-full px-3 py-1">
                  <div className="text-warning900 w-full truncate text-center text-xs">
                    Swing data missing. Analyze via iOS app when connected to
                    internet.
                  </div>
                </div>
              </td>
            )}
          </>
        ) : (
          <>
            {cells.map((cell) => (
              <Cell cell={cell} key={cell.id} />
            ))}
          </>
        )}
      </tr>
    );
  };

  const renderRow = onRowRender || defaultRowRender;

  if (isLoading) {
    return (
      <div className="flex h-[50vh] items-center justify-center">
        <Spinner color="primary500" size="md" />
      </div>
    );
  }

  return (
    <div className={`w-full bg-white ${containerClassName}`}>
      <div
        className={`border-gray100 overflow-auto border-x ${
          !showPagination ? "rounded-b-xl border-b" : ""
        } ${tableClassName}`}
        onMouseDown={dragScroll.onMouseDown}
        onMouseLeave={dragScroll.onMouseLeave}
        onMouseMove={dragScroll.onMouseMove}
        onMouseUp={dragScroll.onMouseUp}
        ref={dragScroll.ref}
      >
        <DndContext
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <table className="w-full table-fixed border-collapse">
            <thead className="bg-white">
              <SortableContext
                items={
                  table.getHeaderGroups()[0]?.headers.map((h) => h.id) ?? []
                }
                strategy={verticalListSortingStrategy}
              >
                {table.getHeaderGroups().map((headerGroup) => (
                  <tr key={headerGroup.id}>
                    {headerGroup.headers.map((header, index) => (
                      <SortableHeader
                        columnIndex={index}
                        header={header}
                        key={header.id}
                      />
                    ))}
                  </tr>
                ))}
              </SortableContext>
            </thead>

            <tbody className="text-sm">
              {/* Average rows */}

              <AvgRows
                allVideos={allVideos}
                averages={averages}
                columnOrder={columnOrder}
                columns={columns}
                selectedTableData={
                  enableRowSelection ? selectedTableData : undefined
                }
              />

              {/* Additional rows (e.g., CollectionsRow) */}
              {(additionalRows || [])
                .filter(isComponent)
                .map((RowComponent, index) => (
                  <RowComponent key={`additional-row-${index}`} />
                ))}

              {/* Data rows */}
              {table.getRowModel().rows.map((row) => renderRow(row))}
            </tbody>
          </table>
        </DndContext>
      </div>

      {/* Pagination */}
      {showPagination && (
        <Pagination
          className="border-gray200 rounded-b-xl border px-6 py-4"
          currentPage={pagination.page}
          onPageChange={(page) => setPagination({ page })}
          onRowsPerPageChange={(rowsPerPage) => setPagination({ rowsPerPage })}
          pageNumber={pagination.totalPages}
          rowsPerPage={pagination.rowsPerPage}
          showItemsPerPage={(allVideos?.length || 0) > 0}
        />
      )}
    </div>
  );
}
